import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import type { ImageColumnProps } from '@modules/page-builder/types';
import {
    TRIO_COLUMN_WIDTH_MD,
    TRIO_COLUMN_WIDTH,
} from '@modules/page-builder/config/column-constants';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import * as S from './TrioImageModule.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import ImageWithPaintPicker from '../../common/image-with-paint-picker/ImageWithPaintPicker';
import { ImageModuleExtraSpacing } from '@modules/page-builder/component/detail/detail-modules/DetailModules.style';

interface TrioImageModuleProps {
    columns: ImageColumnProps[];
}

/**
 * A rendered Page Builder module for displaying three images in a grid layout.
 *
 * The module displays three images side by side on desktop and stacked on mobile.
 * Each image can have captions, tags, and interactive elements like paint picker integration.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/trio-image-module/TrioImageModule.tsx}
 */
const TrioImageModule = ({ columns }: TrioImageModuleProps) => {
    const { mobile } = useAppDisplayModeContext();

    const { css } = useStyles({
        columns: columns.filter((c) => mobile && c.imageUrl).length,
        mobile,
    });

    return (
        <div
            className={css(S.TrioImageModuleContainer, ImageModuleExtraSpacing)}
        >
            {columns.map((column) => {
                const isMedium = column.dimension.width >= TRIO_COLUMN_WIDTH_MD;
                const isSmall = !isMedium;
                if (mobile && !column.imageUrl) {
                    return null;
                }

                return (
                    <div
                        key={column.id}
                        className={css(S.TrioImageContainerImage(isMedium))}
                    >
                        {column?.imageUrl && (
                            <DetailSingleImageShell
                                caption={column.caption}
                                dimension={{
                                    ...column.dimension,
                                    width: TRIO_COLUMN_WIDTH,
                                    height: 850,
                                }}
                                libraryImage={column?.libraryImage}
                                tags={column.tags}
                                uniqueApprovedTags={column.uniqueApprovedTags}
                                imageURL={column.imageUrl}
                            >
                                <ImageWithPaintPicker
                                    src={column.imageUrl}
                                    width={column.dimension.width}
                                    height={column.dimension.height}
                                    layout={'fill'}
                                    objectFit={'cover'}
                                    className="!rounded-none"
                                    sizes={
                                        isSmall
                                            ? getImagesSizes(400)
                                            : getImagesSizes(618)
                                    }
                                    alt={
                                        column.caption ||
                                        column?.libraryImage?.Title ||
                                        ''
                                    }
                                />
                            </DetailSingleImageShell>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default TrioImageModule;
